import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SafeMotion from "@/components/motion/SafeMotion";
import MotionErrorBoundary from "@/components/motion/MotionErrorBoundary";
import { Zap, Facebook, Twitter, Instagram, Youtube, Mail, Phone, MapPin, Heart, Shield, Truck, Crown, Loader2, CheckCircle2 } from "lucide-react";
import { useMobileViewport } from "@/hooks/useMobileViewport";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Footer = () => {
  const { effectiveTheme } = useTheme();
  const viewportInfo = useMobileViewport();
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const footerLinks = {
    shop: [{
      name: 'All Products',
      href: '/products'
    }, {
      name: 'New Arrivals',
      href: '/products?filter=new'
    }, {
      name: 'Best Sellers',
      href: '/products?filter=popular'
    }, {
      name: 'Sale',
      href: '/products?filter=sale'
    }, {
      name: 'Gift Cards',
      href: '/gift-cards'
    }],
    company: [{
      name: 'About Us',
      href: '/about'
    }, {
      name: 'Careers',
      href: '/careers'
    }, {
      name: 'Press',
      href: '/press'
    }, {
      name: 'Sustainability',
      href: '/sustainability'
    }, {
      name: 'Investor Relations',
      href: '/investors'
    }],
    support: [{
      name: 'Help Center',
      href: '/help'
    }, {
      name: 'Contact Us',
      href: '/contact'
    }, {
      name: 'Size Guide',
      href: '/size-guide'
    }, {
      name: 'Shipping Info',
      href: '/shipping'
    }, {
      name: 'Returns',
      href: '/returns'
    }],
    legal: [{
      name: 'Privacy Policy',
      href: '/privacy'
    }, {
      name: 'Terms of Service',
      href: '/terms'
    }, {
      name: 'Cookie Policy',
      href: '/cookies'
    }, {
      name: 'Accessibility',
      href: '/accessibility'
    }]
  };
  const socialLinks = [{
    name: 'Facebook',
    href: '#',
    icon: Facebook
  }, {
    name: 'Twitter',
    href: '#',
    icon: Twitter
  }, {
    name: 'Instagram',
    href: '#',
    icon: Instagram
  }, {
    name: 'YouTube',
    href: '#',
    icon: Youtube
  }];
  const features = [{
    icon: Shield,
    text: 'Secure Payments'
  }, {
    icon: Truck,
    text: 'Free Shipping'
  }, {
    icon: Crown,
    text: 'Premium Quality'
  }, {
    icon: Heart,
    text: '24/7 Support'
  }];
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <MotionErrorBoundary>
      <footer 
        className={`relative z-[100] min-h-[200px] bg-card/95 backdrop-blur-md block ${viewportInfo.isActualMobile ? 'mobile-footer-force' : ''}`}
        style={{ 
          willChange: 'transform',
          transform: 'translate3d(0,0,0)',
          WebkitTransform: 'translate3d(0,0,0)',
          paddingBottom: 'clamp(1.5rem, 1.5rem + env(safe-area-inset-bottom, 0px), 4rem)',
          background: "linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--card) / 0.98) 100%)"
        } as React.CSSProperties}
      >
        {/* Subtle top gradient overlay for integration */}
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-background/60 via-background/20 to-transparent pointer-events-none" />
        
        {/* Enhanced border with gradient */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        
        <SafeMotion 
          variants={containerVariants} 
          initial="hidden" 
          animate="visible" 
          className="w-full px-4 md:px-8 lg:px-12 relative"
          fallback={<div className="w-full px-4 md:px-8 lg:px-12 relative"></div>}
        >
          {/* Features Section with enhanced styling */}
          <SafeMotion 
            variants={itemVariants} 
            className="border-b border-border/50 py-12 relative"
            fallback={<div className="border-b border-border/50 py-12 relative"></div>}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 px-4 sm:px-6 lg:px-8">
              {features.map((feature, index) => 
                <SafeMotion 
                  key={feature.text} 
                  className="flex items-center space-x-3 p-4 bg-gradient-to-br from-muted/40 to-muted/20 rounded-xl hover:from-muted/60 hover:to-muted/30 transition-all duration-300 backdrop-blur-sm border border-border/30" 
                  whileHover={{ scale: 1.02, y: -2 }} 
                  transition={{ type: "spring", stiffness: 300 }}
                  fallback={
                    <div className="flex items-center space-x-3 p-4 bg-gradient-to-br from-muted/40 to-muted/20 rounded-xl hover:from-muted/60 hover:to-muted/30 transition-all duration-300 backdrop-blur-sm border border-border/30">
                      <feature.icon className="h-6 w-6 text-primary flex-shrink-0" />
                      <span className="text-sm font-medium text-foreground">{feature.text}</span>
                    </div>
                  }
                >
                  <feature.icon className="h-6 w-6 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground">{feature.text}</span>
                </SafeMotion>
              )}
            </div>
          </SafeMotion>

          {/* Main Footer Content with enhanced spacing */}
          <div className="px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
              {/* Company Info with enhanced styling */}
              <SafeMotion variants={itemVariants} className="lg:col-span-2" fallback={<div className="lg:col-span-2"></div>}>
                <Link to="/" className="flex items-center space-x-2 mb-6 group">
                  <div className="h-8 w-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                    <Zap className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <span className="text-xl font-bold text-foreground">Maison</span>
                </Link>
                
                <p className="text-muted-foreground mb-6 max-w-sm leading-relaxed">
                  Experience exceptional quality and extraordinary service with our curated collection of premium products.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 text-muted-foreground hover:text-foreground transition-colors group">
                    <Mail className="h-4 w-4 group-hover:scale-110 transition-transform" />
                    <span className="text-sm">service@maison.com</span>
                  </div>
                  <div className="flex items-center space-x-3 text-muted-foreground hover:text-foreground transition-colors group">
                    <Phone className="h-4 w-4 group-hover:scale-110 transition-transform" />
                    <span className="text-sm">+1 (555) 123-4567</span>
                  </div>
                  <div className="flex items-center space-x-3 text-muted-foreground hover:text-foreground transition-colors group">
                    <MapPin className="h-4 w-4 group-hover:scale-110 transition-transform" />
                    <span className="text-sm">123 Premium St, Quality City</span>
                  </div>
                </div>
              </SafeMotion>

              {/* Navigation Links with enhanced hover effects */}
              <SafeMotion variants={itemVariants} fallback={<div></div>}>
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4">Shop</h3>
                <ul className="space-y-3">
                  {footerLinks.shop.map(link => 
                    <li key={link.name}>
                      <Link to={link.href} className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-sm relative group">
                        <span className="relative z-10">{link.name}</span>
                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left"></span>
                      </Link>
                    </li>
                  )}
                </ul>
              </SafeMotion>

              <SafeMotion variants={itemVariants} fallback={<div></div>}>
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4">Company</h3>
                <ul className="space-y-3">
                  {footerLinks.company.map(link => 
                    <li key={link.name}>
                      <Link to={link.href} className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-sm relative group">
                        <span className="relative z-10">{link.name}</span>
                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left"></span>
                      </Link>
                    </li>
                  )}
                </ul>
              </SafeMotion>

              <SafeMotion variants={itemVariants} fallback={<div></div>}>
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4">Support</h3>
                <ul className="space-y-3">
                  {footerLinks.support.map(link => 
                    <li key={link.name}>
                      <Link to={link.href} className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-sm relative group">
                        <span className="relative z-10">{link.name}</span>
                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left"></span>
                      </Link>
                    </li>
                  )}
                </ul>
              </SafeMotion>

              {/* Newsletter with enhanced styling */}
              <SafeMotion variants={itemVariants} fallback={<div></div>}>
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4">Newsletter</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Subscribe to get updates on new products and exclusive offers.
                </p>
                <form 
                  className="space-y-3" 
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!email || !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
                      toast.error("Please enter a valid email address");
                      return;
                    }
                    
                    setIsSubscribing(true);
                    try {
                      const { data, error } = await supabase.functions.invoke("subscribe-newsletter", {
                        body: { email },
                      });

                      if (error) throw error;

                      if (data.success) {
                        if (data.alreadySubscribed) {
                          toast.info(data.message);
                        } else {
                          toast.success(data.message, {
                            icon: <CheckCircle2 className="h-5 w-5" />,
                          });
                        }
                        setEmail("");
                      } else {
                        toast.error(data.error || "Failed to subscribe");
                      }
                    } catch (error: any) {
                      console.error("Newsletter subscription error:", error);
                      toast.error(error.message || "Failed to subscribe to newsletter");
                    } finally {
                      setIsSubscribing(false);
                    }
                  }}
                >
                  <Input 
                    type="email" 
                    placeholder="Enter your email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubscribing}
                    className="bg-background/50 border-input backdrop-blur-sm focus:bg-background transition-colors" 
                  />
                  <Button 
                    type="submit"
                    disabled={isSubscribing}
                    className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200" 
                    size="sm"
                  >
                    {isSubscribing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Subscribing...
                      </>
                    ) : (
                      "Subscribe"
                    )}
                  </Button>
                </form>
              </SafeMotion>
            </div>
          </div>

          {/* Bottom Section with enhanced styling */}
          <SafeMotion 
            variants={itemVariants} 
            className="border-t border-border/50 px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-r from-muted/20 to-muted/10 backdrop-blur-sm" 
            fallback={<div className="border-t border-border/50 px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-r from-muted/20 to-muted/10 backdrop-blur-sm"></div>}
          >
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-muted-foreground text-sm">
                © {new Date().getFullYear()} VRAM. All rights reserved.
              </div>

              <div className="flex space-x-6">
                {footerLinks.legal.map(link => 
                  <Link key={link.name} to={link.href} className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-sm relative group">
                    <span className="relative z-10">{link.name}</span>
                    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left"></span>
                  </Link>
                )}
              </div>

              <div className="flex space-x-4">
                {socialLinks.map(social => 
                  <SafeMotion 
                    key={social.name} 
                    as="a" 
                    href={social.href} 
                    className="text-muted-foreground hover:text-foreground transition-colors duration-200 p-2 rounded-full hover:bg-accent/50" 
                    whileHover={{ scale: 1.1, rotate: 5 }} 
                    whileTap={{ scale: 0.95 }}
                    fallback={
                      <a href={social.href} className="text-muted-foreground hover:text-foreground transition-colors duration-200 p-2 rounded-full hover:bg-accent/50">
                        <span className="sr-only">{social.name}</span>
                        <social.icon className="h-5 w-5" />
                      </a>
                    }
                  >
                    <span className="sr-only">{social.name}</span>
                    <social.icon className="h-5 w-5" />
                  </SafeMotion>
                )}
              </div>
            </div>
          </SafeMotion>
        </SafeMotion>
      </footer>
    </MotionErrorBoundary>
  );
};

export default Footer;
